import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { Messages } from '../../codegen/Localize';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { AppState } from '../../app/state/AppState';
import { PageHeaderButtonType } from '../../app/layout/PageHeader';
import { MapDispatchToProps, MapStateToProps } from '../../app//utils/ConnectUtils';
import { NotificationPanel, NotificationDisplayMode, UserNotificationPanel } from './NotificationPanel';
import { DropdownButtonType, DropdownButton } from '@tableau/widgets';
import { NotificationCenterStyles } from '../styles/notificationCenterStyles';
import { Icons } from './Icons';
import { IconProps } from '../../common/react/Icons';
import { UserNotificationActions } from '../redux/UserNotificationActions';
import { defaultMemoize } from 'reselect';
import { SessionUserSelectors, SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { sendUserNotificationsCenterTelemetry, UserNotificationsTelemetryEvents } from '../telemetry/UserNotificationsTelemetry';

/** Manages periodic execution of an action. */
class PeriodicAction {
  private intervalId: NodeJS.Timeout;

  constructor(private action: () => void, private intervalSeconds: number) { }

  /** Starts the periodic execution. Will wait for the wait interval before the first call of action. */
  public start() {
    if (!this.intervalId) {
      this.intervalId = setInterval(this.action, this.intervalSeconds * 1000);
    }
  }

  /** Stops the periodic execution. */
  public stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  /** Calls the action once. */
  public call() {
    this.action();
  }
}

export interface NotificationCenterOwnProps {
  buttonType: PageHeaderButtonType;
}

export interface NotificationCenterPropsFromReduxDispatch {
  readonly userNotificationActions: UserNotificationActions;
}

export interface NotificationCenterPropsFromReduxState {
  readonly hasUnseenNotifications: boolean;
  readonly siteUrls: SiteUrls;
  readonly notificationDisplayMode: NotificationDisplayMode;
  readonly userName: string;
  readonly domainName: string;
  readonly userId: string;
  readonly siteLuid: string;
}

export type NotificationCenterComponentProps = NotificationCenterOwnProps & NotificationCenterPropsFromReduxDispatch & NotificationCenterPropsFromReduxState;

export class NotificationCenterComponent extends React.PureComponent<NotificationCenterComponentProps> {
  private checkHasUnseenNotificationPoller: PeriodicAction;
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();

  constructor(props: NotificationCenterComponentProps) {
    super(props);
    this.checkHasUnseenNotificationPoller =
      new PeriodicAction(() => this.props.userNotificationActions.checkHasUnseenUserNotifications(), 60);
  }

  private onDropdownOpen() {
    //TODO make fetch call only needed
    sendUserNotificationsCenterTelemetry(UserNotificationsTelemetryEvents.openNotificationCenter, this.props.userId, this.props.siteLuid);
    this.props.userNotificationActions.getUserNotifications();
  }

  private closePanel = (): void => {
    if (this.dropdownButtonRef.current) {
      sendUserNotificationsCenterTelemetry(UserNotificationsTelemetryEvents.closeNotificationCenter, this.props.userId, this.props.siteLuid);
      this.dropdownButtonRef.current.toggle(false);
    }
  }

  public render() {

    return (
      <DropdownButton
        aria-controls={'notification-panel'}
        aria-haspopup={'dialog'}
        aria-label={Messages.userNotificationsNotificationCenter()}
        closeOnPopupBlur={true}
        focusOnClose={true}
        useDarkStyling={true}
        ref={this.dropdownButtonRef}
        label={undefined}
        testId={'notification-center'}
        type={DropdownButtonType.Icon}
        children={this.getIconButtonContent()}
        popupContent={<UserNotificationPanel
          siteUrls={this.props.siteUrls}
          userNotificationActions={this.props.userNotificationActions}
          displayMode={this.props.notificationDisplayMode}
          closePanel={this.closePanel}
          userName={this.props.userName}
          domainName={this.props.domainName}
          userId={this.props.userId}
          siteLuid={this.props.siteLuid}
        />}
        className={NotificationCenterStyles.dropdownButton}
        onOpen={this.onDropdownOpen.bind(this)}
        popupAlignment={'right'}
      />);
  }

  public componentDidMount() {
    this.checkHasUnseenNotificationPoller.call();
    this.checkHasUnseenNotificationPoller.start();
  }

  public componentWillUnmount() {
    this.checkHasUnseenNotificationPoller.stop();
  }

  private getIconButtonContent(): JSX.Element {
    var iconProps: IconProps = {
      // TODO: finalize text
      title: Messages.alertsTitle()
    };
    if (this.props.buttonType === 'light') {
      return this.props.hasUnseenNotifications
        ? <Icons.NotificationCenterLightWithUnseenNotifications {...iconProps} testId='notification-center-icon-light-unseen' />
        : <Icons.NotificationCenterLight {...iconProps} testId='notification-center-icon-light' />;
    } else {
      return this.props.hasUnseenNotifications
        ? <Icons.NotificationCenterWithUnseenNotifications {...iconProps} testId='notification-center-icon-regular-unseen' />
        : <Icons.NotificationCenter {...iconProps} testId='notification-center-icon-regular' />;
    }
  }


}

export namespace ReduxConnectedNotificationCenter {

  const mapStateToProps: MapStateToProps<NotificationCenterPropsFromReduxState> = (state: AppState) => ({
    // TFSID: 1150022 - implement hasUnseenNotifications state
    hasUnseenNotifications: state.notificationState.hasUnseenNotifications,
    siteUrls: siteUrlsSelector(state),
    notificationDisplayMode: state.notificationState.notificationDisplayMode,
    userName: SessionUserSelectors.sessionUserName(state),
    domainName: SessionUserSelectors.sessionDomainName(state),
    userId: SessionUserSelectors.sessionUserId(state),
    siteLuid: SessionSiteSelectors.siteLuid(state)
  });

  const mapDispatchToProps: MapDispatchToProps<NotificationCenterPropsFromReduxDispatch> = (dispatch: Dispatch<AppState>) => ({
    userNotificationActions: memoizedNotificationActions(dispatch),
  });

  const memoizedNotificationActions = defaultMemoize((dispatch: Dispatch<AppState>) => new UserNotificationActions(dispatch));
  export const HOC = connect(mapStateToProps, mapDispatchToProps);
}


export const NotificationCenter: React.ComponentType<NotificationCenterOwnProps> = ReduxConnectedNotificationCenter.HOC(NotificationCenterComponent);


